import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Search, ChevronLeft, ChevronRight, Upload, Trash2, Archive, Download, X, Tag, Pencil, Plus } from "lucide-react";
import LeadTypePill from "@/components/LeadTypePill";
import BulkUpdateLeadTypesDialog from "@/components/BulkUpdateLeadTypesDialog";
import CreateLeadDialog from "@/components/CreateLeadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { leadPhoneDisplay } from "@/lib/phone";

const DATE_OPTIONS = [
    { value: "all", label: "All time" },
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "60d", label: "Last 60 days" },
    { value: "90d", label: "Last 90 days" },
];

const leadName = (l) => `${l.first_name || ""} ${l.last_name || ""}`.trim();

// Row click opens the lead unless the click hit a control or the user is selecting text.
const INTERACTIVE = "a,button,input,label,select,textarea,[role=checkbox],[role=button],[data-row-ignore]";

export default function Leads() {
    const navigate = useNavigate();
    const openRow = (e, id) => {
        if (e.target.closest(INTERACTIVE)) return;
        if (window.getSelection()?.toString()) return;
        if (e.metaKey || e.ctrlKey) { window.open(`/leads/${id}`, "_blank"); return; }
        navigate(`/leads/${id}`);
    };
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [leadStatusFilter, setLeadStatusFilter] = useState("all");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [unassignedOnly, setUnassignedOnly] = useState(false);
    const [reviewCount, setReviewCount] = useState(null);
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";  // W1.6: simple lead-only status filter
    const [dateFilter, setDateFilter] = useState("all");
    const [types, setTypes] = useState([]);

    // selection: Set of ids on this page; allFiltered=true means apply current filter on backend
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [allFiltered, setAllFiltered] = useState(false);

    // confirmation modal
    const [confirm, setConfirm] = useState(null);
    // bulk Update Lead Types modal — mixed-state checkboxes against the current selection
    const [updateTypesOpen, setUpdateTypesOpen] = useState(false);
    // F2: Create Lead modal
    const [createLeadOpen, setCreateLeadOpen] = useState(false);

    useEffect(() => { api.get("/lead-types").then(r => setTypes(r.data)); }, []);

    const filterForApi = () => {
        const f = {};
        if (q) f.q = q;
        if (typeFilter !== "all") f.lead_type_id = typeFilter;
        if (statusFilter !== "all") f.status = statusFilter;
        if (dateFilter !== "all") f.date_filter_kind = dateFilter;
        // W1.6: lead_status is the simple lead-management status. Filter
        // values map 1:1 to the 5 allowed lead statuses (plus "all").
        if (leadStatusFilter !== "all") f.lead_status = leadStatusFilter;
        if (sourceFilter !== "all") f.source = sourceFilter;
        if (unassignedOnly) f.unassigned = true;
        return f;
    };

    const load = async () => {
        const { data } = await api.get("/leads", { params: { ...filterForApi(), page, page_size: pageSize } });
        setLeads(data.items);
        setTotal(data.total);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, typeFilter, statusFilter, leadStatusFilter, dateFilter, sourceFilter, unassignedOnly]);
    useEffect(() => {
        if (!isAdmin) return;
        api.get("/website-reviews/count").then((r) => setReviewCount(r.data.pending)).catch(() => setReviewCount(null));
    }, [isAdmin]);

    const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

    const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const websiteFilter = sourceFilter === "A+ Website";
    const filterActive = q || typeFilter !== "all" || statusFilter !== "all" || dateFilter !== "all" || leadStatusFilter !== "all" || sourceFilter !== "all" || unassignedOnly;

    const togglePageSelect = (checked) => {
        if (checked) {
            const next = new Set(selectedIds);
            leads.forEach(l => next.add(l.id));
            setSelectedIds(next);
        } else {
            const next = new Set(selectedIds);
            leads.forEach(l => next.delete(l.id));
            setSelectedIds(next);
            setAllFiltered(false);
        }
    };
    const toggleRow = (id, checked) => {
        const next = new Set(selectedIds);
        checked ? next.add(id) : next.delete(id);
        setSelectedIds(next);
        if (!checked) setAllFiltered(false);
    };
    const clearSelection = () => { setSelectedIds(new Set()); setAllFiltered(false); };

    const pageAllSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.id));
    const effectiveCount = allFiltered ? total : selectedIds.size;

    const runConfirmedAction = async (action, label) => {
        // action: "delete" | "archive"
        try {
            if (action === "export") {
                const body = allFiltered ? { filter: filterForApi() } : { ids: Array.from(selectedIds) };
                const res = await api.post("/leads/export", body, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url; a.download = "leads_export.csv";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${effectiveCount.toLocaleString()} leads`);
                return;
            }
            const body = allFiltered
                ? { action, filter: filterForApi(), confirm_count: total }
                : { action, ids: Array.from(selectedIds) };
            const { data } = await api.post("/leads/bulk-action", body);
            toast.success(`${data.count.toLocaleString()} leads ${data.action === "delete" ? "deleted" : "archived"}`);
            clearSelection();
            await load();
            // refresh lead type counts
            api.get("/lead-types").then(r => setTypes(r.data));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const askConfirm = (action) => {
        const count = effectiveCount;
        if (count === 0) return;
        if (action === "export") return runConfirmedAction("export");
        const verb = action === "delete" ? "Delete" : "Archive";
        const ofType = (allFiltered && typeFilter !== "all") ? ` ${typeById[typeFilter]?.name ?? ""}` : "";
        setConfirm({
            title: `${verb} ${count.toLocaleString()}${ofType} leads?`,
            body: action === "delete"
                ? "This action cannot be undone. Skywave remains the source of truth — these leads can be re-imported anytime."
                : "Archived leads stay in the system but won't be targeted by campaigns. You can re-activate later via filter.",
            destructive: action === "delete",
            confirmLabel: verb,
            run: () => runConfirmedAction(action),
        });
    };

    const askDeleteAllFiltered = () => {
        // open confirm using current filter — equivalent to "select all filtered + delete"
        const count = total;
        const ofType = typeFilter !== "all" ? ` ${typeById[typeFilter]?.name ?? ""}` : "";
        const dateLabel = dateFilter !== "all" ? ` imported in ${DATE_OPTIONS.find(d => d.value === dateFilter)?.label.toLowerCase()}` : "";
        setConfirm({
            title: `Delete ${count.toLocaleString()}${ofType} leads${dateLabel}?`,
            body: "This action cannot be undone. Skywave remains the source of truth — these leads can be re-imported anytime.",
            destructive: true,
            confirmLabel: "Delete",
            run: async () => {
                try {
                    const { data } = await api.post("/leads/bulk-action", { action: "delete", filter: filterForApi(), confirm_count: total });
                    toast.success(`${data.count.toLocaleString()} leads deleted`);
                    clearSelection();
                    await load();
                    api.get("/lead-types").then(r => setTypes(r.data));
                } catch (e) {
                    toast.error(formatApiError(e));
                }
            },
        });
    };

    return (
        <div className="space-y-6" data-testid="leads-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Leads</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {total.toLocaleString()} matching · operational pool · Skywave is the source of truth.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <Link to="/admin/website-reviews" data-testid="website-reviews-link">
                            <Button variant="outline" size="sm" className={reviewCount ? "border-amber-400 text-amber-800" : ""}>
                                Website reviews ({reviewCount || 0})
                            </Button>
                        </Link>
                    )}
                    <Link to="/lead-types">
                        <Button variant="outline" size="sm" data-testid="manage-lead-types-button">Lead Types</Button>
                    </Link>
                    <Button size="sm" variant="outline" onClick={() => setCreateLeadOpen(true)} data-testid="create-lead-button">
                        <Plus className="h-4 w-4 mr-1.5" /> Create Lead
                    </Button>
                    <Link to="/imports/new">
                        <Button size="sm" data-testid="import-leads-button">
                            <Upload className="h-4 w-4 mr-1.5" /> Import
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
                {/* Filters */}
                <div className="p-4 border-b border-border flex flex-col md:flex-row gap-3 md:items-center">
                    <form onSubmit={onSearch} className="relative flex-1 min-w-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search name, phone, email, case ID…"
                            className="pl-9"
                            data-testid="leads-search-input"
                        />
                    </form>
                    <div className="flex gap-2 flex-wrap">
                        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); clearSelection(); }}>
                            <SelectTrigger className="w-[180px]" data-testid="filter-lead-type">
                                <SelectValue placeholder="All types" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All types</SelectItem>
                                {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.lead_count})</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={dateFilter} onValueChange={(v) => { setDateFilter(v); setPage(1); clearSelection(); }}>
                            <SelectTrigger className="w-[160px]" data-testid="filter-date">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DATE_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); clearSelection(); }}>
                            <SelectTrigger className="w-[140px]" data-testid="filter-status">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="replied">Replied</SelectItem>
                                <SelectItem value="opted_out">Opted Out</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={leadStatusFilter} onValueChange={(v) => { setLeadStatusFilter(v); setPage(1); clearSelection(); }}>
                            <SelectTrigger className="w-[160px]" data-testid="filter-lead-status">
                                <SelectValue placeholder="Lifecycle" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="new_lead">New Lead</SelectItem>
                                <SelectItem value="paid_lead">Paid Lead</SelectItem>
                                <SelectItem value="follow_up">Follow Up</SelectItem>
                                <SelectItem value="shark_tank">Shark Tank</SelectItem>
                                <SelectItem value="hot_lead">Hot Lead</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); clearSelection(); }}>
                            <SelectTrigger className="w-[150px]" data-testid="filter-source">
                                <SelectValue placeholder="Source" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All sources</SelectItem>
                                <SelectItem value="A+ Website">A+ Website</SelectItem>
                                <SelectItem value="Voicemail">Voicemail</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button size="sm" variant={unassignedOnly ? "default" : "outline"} data-testid="filter-unassigned"
                                onClick={() => { setUnassignedOnly((v) => !v); setPage(1); clearSelection(); }}>
                            Unassigned
                        </Button>
                    </div>
                </div>

                {/* Bulk toolbar */}
                {selectedIds.size > 0 ? (
                    <div className="px-4 py-2.5 bg-primary/5 border-b border-primary/20 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="bulk-toolbar">
                        <div className="text-sm text-gray-900">
                            <span className="font-medium tabular">{effectiveCount.toLocaleString()}</span> selected
                            {!allFiltered && total > selectedIds.size && (
                                <button
                                    onClick={() => setAllFiltered(true)}
                                    className="ml-3 text-primary hover:underline text-sm font-medium"
                                    data-testid="select-all-filtered"
                                >
                                    Select all {total.toLocaleString()} filtered
                                </button>
                            )}
                            {allFiltered && (
                                <span className="ml-3 text-xs text-muted-foreground">(all filtered selected)</span>
                            )}
                        </div>
                        <div className="flex gap-2 ml-auto flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => setUpdateTypesOpen(true)} data-testid="bulk-update-types">
                                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Update Lead Types
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => askConfirm("export")} data-testid="bulk-export">
                                <Download className="h-3.5 w-3.5 mr-1.5" /> Export
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => askConfirm("archive")} data-testid="bulk-archive">
                                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
                            </Button>
                            {!websiteFilter && (
                                <Button variant="outline" size="sm" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200" onClick={() => askConfirm("delete")} data-testid="bulk-delete">
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                                </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={clearSelection} data-testid="clear-selection" aria-label="Clear selection">
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ) : filterActive && (
                    <div className="px-4 py-2.5 bg-gray-50/70 border-b border-border flex items-center justify-between gap-2 text-sm">
                        <div className="text-muted-foreground">
                            Filter applied · {total.toLocaleString()} matching
                        </div>
                        {websiteFilter ? (
                            <span className="text-xs text-muted-foreground" data-testid="website-bulk-delete-disabled">
                                Bulk delete is off for A+ Website leads
                            </span>
                        ) : (
                            <Button variant="ghost" size="sm" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50" onClick={askDeleteAllFiltered} data-testid="delete-all-filtered">
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete all filtered
                            </Button>
                        )}
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/70 text-left">
                            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                <th className="pl-4 pr-2 py-3 w-8">
                                    <Checkbox
                                        checked={pageAllSelected}
                                        onCheckedChange={togglePageSelect}
                                        aria-label="Select page"
                                        data-testid="select-page"
                                    />
                                </th>
                                <th className="px-4 py-3 font-medium">A+ CaseFlow</th>
                                <th className="px-4 py-3 font-medium">Name</th>
                                <th className="px-4 py-3 font-medium">Case ID</th>
                                <th className="px-4 py-3 font-medium">Phone</th>
                                <th className="px-4 py-3 font-medium">Email</th>
                                <th className="px-4 py-3 font-medium">Type</th>
                                <th className="px-4 py-3 font-medium">Imported</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground" data-testid="leads-empty">
                                        No leads match your filters.
                                    </td>
                                </tr>
                            )}
                            {leads.map(l => {
                                const checked = selectedIds.has(l.id);
                                return (
                                    <tr
                                        key={l.id}
                                        tabIndex={0}
                                        aria-label={`Open lead ${leadName(l) || leadPhoneDisplay(l) || l.acos_id || ""}`}
                                        onClick={e => openRow(e, l.id)}
                                        onKeyDown={e => { if (e.key === "Enter" && e.target === e.currentTarget) navigate(`/leads/${l.id}`); }}
                                        className={`border-t border-border cursor-pointer hover:bg-primary/5 focus-visible:outline-none focus-visible:bg-primary/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 transition-colors ${checked ? "bg-primary/5" : ""}`}
                                        data-testid={`lead-row-${l.id}`}
                                    >
                                        <td className="pl-4 pr-2 py-3 w-8" onClick={e => e.stopPropagation()}>
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={(c) => toggleRow(l.id, c)}
                                                aria-label="Select row"
                                                data-testid={`select-row-${l.id}`}
                                            />
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-700" data-testid={`lead-acos-${l.id}`} data-acos-cell="">
                                            {l.acos_id != null ? l.acos_id : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Link to={`/leads/${l.id}`} className={leadName(l) ? "text-gray-900 font-medium hover:text-primary" : "text-gray-400 italic hover:text-primary"} data-testid={`lead-name-link-${l.id}`}>
                                                {leadName(l) || "Unnamed caller"}
                                            </Link>
                                            {l.source === "A+ Website" && (
                                                <span className="ml-2 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border bg-sky-50 text-sky-800 border-sky-200" data-testid={`lead-website-badge-${l.id}`}>
                                                    A+ Website{!l.processor_user_id ? " · unassigned" : ""}
                                                </span>
                                            )}
                                            {l.lead_status === "converted" && (
                                                <span className="inline-flex items-center gap-1 ml-2 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    converted
                                                    {l.client_id && (
                                                        <Link to={`/clients/${l.client_id}`} className="underline hover:no-underline ml-1" data-testid={`leads-view-client-${l.id}`}>view client</Link>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[12.5px] text-gray-700" data-testid={`lead-case-id-${l.id}`}>{l.case_id || "—"}</td>
                                        <td className="px-4 py-3 text-gray-700 tabular" title={l.phone_e164 || l.phone || ""}>{leadPhoneDisplay(l) || "—"}</td>
                                        <td className="px-4 py-3 text-gray-700 truncate max-w-[220px]">{l.email || "—"}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {(l.lead_type_ids || []).map(id => typeById[id] && (
                                                    <LeadTypePill key={id} leadType={typeById[id]} />
                                                ))}
                                                {(!l.lead_type_ids || l.lead_type_ids.length === 0) && (
                                                    <LeadTypePill leadType={null} />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs text-gray-600 capitalize">{l.status?.replace("_", " ")}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="border-t border-border px-4 py-3 flex items-center justify-between text-sm">
                    <div className="text-muted-foreground">Page {page} of {totalPages}</div>
                    <div className="flex gap-1">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                <AlertDialogContent data-testid="confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription className="leading-relaxed">{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-action"
                            onClick={async () => { const fn = confirm?.run; setConfirm(null); if (fn) await fn(); }}
                            className={confirm?.destructive ? "bg-rose-600 hover:bg-rose-700" : ""}
                        >
                            {confirm?.confirmLabel || "Confirm"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <BulkUpdateLeadTypesDialog
                open={updateTypesOpen}
                onOpenChange={setUpdateTypesOpen}
                selectionPayload={allFiltered ? { filter: filterForApi() } : { ids: Array.from(selectedIds) }}
                effectiveCount={effectiveCount}
                onApplied={async () => {
                    clearSelection();
                    await load();
                    api.get("/lead-types").then(r => setTypes(r.data));
                }}
            />
            <CreateLeadDialog
                open={createLeadOpen}
                onOpenChange={setCreateLeadOpen}
                onCreated={() => { /* refresh list — current page may not include new lead, but counter updates */ load(); }}
            />
        </div>
    );
}
